import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import TableModal from "../../../../components/reusableComponent/TableModal";
import CustomDate from "../../../../components/common/CustomDate";
import { useGetEmailFirstResponseClickToExpandQuery } from "../../../../Services/dashboard/dashboard";
import { useEmailDashboardQuery } from "../../../../Services/dashboard/EmailDashboardApi";

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

function formatDateToYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function buildExpandViewDateRange(endDateStr: string) {
  const endDate = parseYyyyMmDdToLocalDate(endDateStr);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (30 - 1));
  return {
    startDate,
    endDate,
    key: "selection" as const,
  };
}

interface DailySLA {
  date: string;
  within_sla: number;
  breached_sla: number;
}

interface EmailFirstResponseSLA {
  overall_within_sla: number;
  overall_breached_sla: number;
  daily_first_response_sla: DailySLA[];
}

interface EmailDashboardData {
  email_first_response_sla?: EmailFirstResponseSLA;
}

/** Performance API may nest under `email_first_response_sla` or expose fields at the root (same as main tab). */
function emptyFirstResponseSla(): EmailFirstResponseSLA {
  return {
    overall_within_sla: 0,
    overall_breached_sla: 0,
    daily_first_response_sla: [],
  };
}

function pickFirstResponseSlaSection(raw: unknown): EmailFirstResponseSLA {
  if (!raw || typeof raw !== "object") return emptyFirstResponseSla();
  const root = raw as Record<string, unknown>;
  const nested = root.email_first_response_sla as
    | EmailFirstResponseSLA
    | undefined;
  if (
    nested &&
    typeof nested === "object" &&
    (Array.isArray(nested.daily_first_response_sla) ||
      nested.overall_within_sla != null ||
      nested.overall_breached_sla != null)
  ) {
    return {
      overall_within_sla: nested.overall_within_sla ?? 0,
      overall_breached_sla: nested.overall_breached_sla ?? 0,
      daily_first_response_sla: Array.isArray(nested.daily_first_response_sla)
        ? nested.daily_first_response_sla
        : [],
    };
  }
  return {
    overall_within_sla: (root.overall_within_sla as number) ?? 0,
    overall_breached_sla: (root.overall_breached_sla as number) ?? 0,
    daily_first_response_sla: Array.isArray(root.daily_first_response_sla)
      ? (root.daily_first_response_sla as DailySLA[])
      : [],
  };
}

interface FirstResponseProps {
  emailDashboardPerformance: EmailDashboardData;
  teamId?: string | null;
  startDate?: string;
  endDate?: string;
}

const toYYYYMMDD = (d: string | Date): string => {
  const date = typeof d === "string" ? new Date(d) : d;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function formatFrResponseChartDate(
  dateString: string,
  format: "short" | "long" = "short",
): string {
  const date = new Date(dateString);
  if (format === "short") {
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
    });
  }
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

type FirstResponseTooltipProps = {
  active?: boolean;
  payload?: any[];
  label?: unknown;
  selectedBars: string[];
};

const FirstResponseTooltip = React.memo(function FirstResponseTooltip({
  active,
  payload,
  label,
  selectedBars,
}: FirstResponseTooltipProps) {
  if (!active || !payload?.length) return null;
  const visiblePayload = payload.filter((p: any) =>
    selectedBars.includes(p.name),
  );
  if (visiblePayload.length === 0) return null;

  const total = visiblePayload.reduce(
    (sum: number, p: any) => sum + (p.value || 0),
    0,
  );

  return (
    <div
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "16px 18px",
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
        minWidth: "220px",
        backdropFilter: "blur(10px)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "12px",
          paddingBottom: "10px",
          borderBottom: "2px solid #e2e8f0",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6366f1"
          strokeWidth="2"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>
          {label as React.ReactNode}
        </span>
      </div>

      <div style={{ marginBottom: "10px" }}>
        {visiblePayload.map((entry: any, index: number) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "8px",
              padding: "6px 8px",
              borderRadius: "6px",
              backgroundColor: `${entry.color}10`,
              transition: "all 0.2s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "4px",
                  backgroundColor: entry.color,
                  boxShadow: `0 0 8px ${entry.color}40`,
                }}
              />
              <span
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: 600,
                }}
              >
                {entry.name}
              </span>
            </div>
            <span
              style={{
                fontSize: "15px",
                fontWeight: 700,
                color: "#1e293b",
              }}
            >
              {entry.value}
            </span>
          </div>
        ))}
      </div>

      {visiblePayload.length > 1 && (
        <div
          style={{
            marginTop: "10px",
            paddingTop: "10px",
            borderTop: "1px dashed #cbd5e1",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: "13px", color: "#64748b", fontWeight: 600 }}>
            Total
          </span>
          <span style={{ fontSize: "16px", fontWeight: 700, color: "#6366f1" }}>
            {total}
          </span>
        </div>
      )}
    </div>
  );
});

type FirstResponseLegendProps = {
  payload?: any[];
  selectedBars: string[];
  onToggle: (barName: string) => void;
};

const FirstResponseLegend = React.memo(function FirstResponseLegend({
  payload: _payload,
  selectedBars,
  onToggle,
}: FirstResponseLegendProps) {
  const legendItems = [
    { value: "Within SL", color: "#10b981" },
    { value: "Breached SL", color: "#ef4444" },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        justifyContent: "center",
        padding: "20px",
        backgroundColor: "#f8fafc",
        borderRadius: "12px",
        marginTop: "20px",
        border: "1px solid #e2e8f0",
      }}
    >
      {legendItems.map((entry: any, index: number) => {
        const isSelected = selectedBars.includes(entry.value);
        return (
          <button
            key={index}
            type="button"
            onClick={() => onToggle(entry.value)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              backgroundColor: isSelected ? "#ffffff" : "#e2e8f0",
              border: isSelected
                ? `2px solid ${entry.color}`
                : "2px solid transparent",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              fontSize: "13px",
              fontWeight: 600,
              color: isSelected ? "#1e293b" : "#94a3b8",
              boxShadow: isSelected ? `0 4px 12px ${entry.color}25` : "none",
              transform: isSelected ? "translateY(-1px)" : "translateY(0)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 6px 16px ${entry.color}30`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = isSelected
                ? "translateY(-1px)"
                : "translateY(0)";
              e.currentTarget.style.boxShadow = isSelected
                ? `0 4px 12px ${entry.color}25`
                : "none";
            }}
          >
            <div
              style={{
                width: "16px",
                height: "16px",
                borderRadius: "4px",
                backgroundColor: isSelected ? entry.color : "#94a3b8",
                transition: "all 0.3s",
                boxShadow: isSelected ? `0 0 8px ${entry.color}50` : "none",
              }}
            />
            {entry.value}
          </button>
        );
      })}
    </div>
  );
});

type FirstResponseFullscreenModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  modalTotalSLA: number;
  modalDateRange: { startDate: Date; endDate: Date; key: string }[];
  setModalDateRange: React.Dispatch<
    React.SetStateAction<{ startDate: Date; endDate: Date; key: string }[]>
  >;
  chartContent: React.ReactNode;
  modalTableData: { date: string; within_sl: number; breached_sl: number; total: number }[];
};

function FirstResponseFullscreenModal({
  isOpen,
  onClose,
  title,
  modalTotalSLA,
  modalDateRange,
  setModalDateRange,
  chartContent,
  modalTableData,
}: FirstResponseFullscreenModalProps) {
  if (!isOpen) return null;

  const theme = {
    colors: {
      text: "#1f2937",
      textMuted: "#6b7280",
      textSecondary: "#4b5563",
      primary: "#6366F1",
    },
  };

  const modalStyles = {
    modalOverlay: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(255, 255, 255, 0.75)",
      zIndex: 9999,
      padding: "24px",
      overflow: "auto",
    },
    modalContent: {
      backgroundColor: "#ffffff",
      borderRadius: "20px",
      width: "95%",
      maxWidth: "1600px",
      margin: "0 auto",
      padding: "32px",
      position: "relative" as const,
      boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "28px",
      paddingBottom: "20px",
      borderBottom: "2px solid #e2e8f0",
    },
  };

  const tableKeys =
    modalTableData && modalTableData.length > 0
      ? Object.keys(modalTableData[0])
      : [];

  return (
    <div style={modalStyles.modalOverlay} onClick={onClose}>
      <div style={modalStyles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.modalHeader}>
          <div>
            <h2
              style={{
                margin: 0,
                color: theme.colors.text,
                fontWeight: 700,
              }}
            >
              {`${title}: ${modalTotalSLA}`}
            </h2>
            <p
              style={{
                margin: "8px 0 0 0",
                color: theme.colors.textMuted,
                fontSize: "14px",
              }}
            >
              Daily first response time SL Matrix
            </p>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <div style={{ position: "relative", zIndex: 10002 }}>
              <CustomDate
                dateRange={modalDateRange}
                setDateRange={setModalDateRange}
                unrestricted
              />
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                fontSize: "24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s",
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "rotate(90deg) scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "rotate(0deg) scale(1)";
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{ marginBottom: "32px" }}>
          <div
            style={{
              width: "100%",
              height: "550px",
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            }}
          >
            {chartContent}
          </div>
        </div>

        {modalTableData && modalTableData.length > 0 && (
          <div style={{ marginTop: "32px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: theme.colors.text,
                  fontSize: "1.5em",
                  fontWeight: 700,
                }}
              >
                📊 Underlying data
              </h3>
              <span
                style={{
                  backgroundColor: theme.colors.primary,
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                {modalTableData.length} rows
              </span>
            </div>

            <div
              style={{
                overflowX: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                maxHeight: "450px",
                overflowY: "auto",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr style={{ backgroundColor: "#f8fafc" }}>
                    {tableKeys.map((key) => (
                      <th
                        key={key}
                        style={{
                          padding: "14px 18px",
                          textAlign: "left",
                          borderBottom: "2px solid #cbd5e1",
                          fontWeight: 700,
                          color: theme.colors.textSecondary,
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          backgroundColor: "#f8fafc",
                        }}
                      >
                        {key.replace(/_/g, " ").toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modalTableData.map((row: any, index: number) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? "#ffffff" : "#f8fafc",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#e0f2fe")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor =
                          index % 2 === 0 ? "#ffffff" : "#f8fafc")
                      }
                    >
                      {tableKeys.map((key) => (
                        <td
                          key={key}
                          style={{
                            padding: "14px 18px",
                            borderBottom: "1px solid #e2e8f0",
                            color: theme.colors.textSecondary,
                          }}
                        >
                          {row[key] !== null && row[key] !== undefined
                            ? row[key] === 0
                              ? "-"
                              : String(row[key])
                            : "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const FirstResponse: React.FC<FirstResponseProps> = ({
  emailDashboardPerformance,
  teamId,
  startDate,
  endDate,
}) => {
  const navigate = useNavigate();
  const [fullscreenChart, setFullscreenChart] = useState<any>(null);
  const [modalDateRange, setModalDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  useEffect(() => {
    if (fullscreenChart && endDate) {
      setModalDateRange([buildExpandViewDateRange(endDate)]);
    }
  }, [fullscreenChart, endDate]);

  const modalRangeStrings = useMemo(
    () => ({
      start: modalDateRange?.[0]?.startDate
        ? formatDateToYyyyMmDd(modalDateRange[0].startDate)
        : "",
      end: modalDateRange?.[0]?.endDate
        ? formatDateToYyyyMmDd(modalDateRange[0].endDate)
        : "",
    }),
    [modalDateRange],
  );

  const { data: modalOverviewData } = useEmailDashboardQuery(
    {
      dashboardType: "performance",
      selectedTeam: teamId ?? "",
      graphStartDate: modalRangeStrings.start,
      graphEndDate: modalRangeStrings.end,
    },
    {
      skip:
        !fullscreenChart ||
        !teamId ||
        !modalRangeStrings.start ||
        !modalRangeStrings.end,
    },
  );
  const [hoveredStat, setHoveredStat] = useState<number | null>(null);
  const [selectedBars, setSelectedBars] = useState<string[]>(["Within SL", "Breached SL"]);
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableModalStage, setTableModalStage] = useState<{ dateStr: string; value: "within_sla" | "breached_sla"; label: string } | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);
  const slaSection = useMemo(
    () => pickFirstResponseSlaSection(emailDashboardPerformance),
    [emailDashboardPerformance],
  );

  const sortedData = useMemo(
    () =>
      [...(slaSection.daily_first_response_sla || [])].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      ),
    [slaSection.daily_first_response_sla],
  );

  const chartData = useMemo(
    () =>
      sortedData.map((item) => {
        const rawDate =
          typeof item.date === "string" && /^\d{4}-\d{2}-\d{2}/.test(item.date)
            ? item.date.slice(0, 10)
            : toYYYYMMDD(item.date);
        return {
          date: formatFrResponseChartDate(String(item.date), "short"),
          fullDate: formatFrResponseChartDate(String(item.date), "long"),
          rawDate,
          "Within SL": item.within_sla,
          "Breached SL": item.breached_sla,
          total: item.within_sla + item.breached_sla,
        };
      }),
    [sortedData],
  );

  const modalPerfRoot = useMemo(
    () =>
      fullscreenChart && modalOverviewData != null
        ? modalOverviewData
        : emailDashboardPerformance,
    [fullscreenChart, modalOverviewData, emailDashboardPerformance],
  );

  const modalSlaSection = useMemo(
    () => pickFirstResponseSlaSection(modalPerfRoot),
    [modalPerfRoot],
  );

  const modalSortedData = useMemo(
    () =>
      [...(modalSlaSection.daily_first_response_sla || [])].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      ),
    [modalSlaSection],
  );

  const modalChartData = useMemo(
    () =>
      modalSortedData.map((item) => {
        const rawDate =
          typeof item.date === "string" && /^\d{4}-\d{2}-\d{2}/.test(item.date)
            ? item.date.slice(0, 10)
            : toYYYYMMDD(item.date);
        return {
          date: formatFrResponseChartDate(String(item.date), "short"),
          fullDate: formatFrResponseChartDate(String(item.date), "long"),
          rawDate,
          "Within SL": item.within_sla,
          "Breached SL": item.breached_sla,
          total: item.within_sla + item.breached_sla,
        };
      }),
    [modalSortedData],
  );

  const modalTotalSLA =
    (modalSlaSection.overall_within_sla || 0) +
    (modalSlaSection.overall_breached_sla || 0);

  const modalTableData = useMemo(
    () =>
      modalSortedData.map((item) => ({
        date: formatFrResponseChartDate(String(item.date), "long"),
        within_sl: item.within_sla,
        breached_sl: item.breached_sla,
        total: item.within_sla + item.breached_sla,
      })),
    [modalSortedData],
  );

  const clickToExpandParams = tableModalOpen && tableModalStage && teamId
    ? {
        graph_name: "daily_first_response_sla",
        value: tableModalStage.value,
        start_date: tableModalStage.dateStr,
        end_date: tableModalStage.dateStr,
        team_id: teamId,
        page: clickToExpandPage,
        page_size: 10,
      }
    : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } = useGetEmailFirstResponseClickToExpandQuery(
    clickToExpandParams ?? { graph_name: "", value: "", start_date: "", end_date: "", team_id: "" },
    { skip: !clickToExpandParams }
  );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw = (clickToExpandData as any)?.results ?? (clickToExpandData as any)?.data ?? clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 10;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [tableModalStage?.dateStr, tableModalStage?.value]);

  const totalSLA = (slaSection.overall_within_sla || 0) + (slaSection.overall_breached_sla || 0);
  const withinSLAPercentage = totalSLA > 0
    ? Math.round(((slaSection.overall_within_sla || 0) / totalSLA) * 100)
    : 0;
  const breachedSLAPercentage = totalSLA > 0
    ? Math.round(((slaSection.overall_breached_sla || 0) / totalSLA) * 100)
    : 0;

  const ResponsiveChartContainer = ResponsiveContainer as any;
  const BarChartContainer = BarChart as any;
  const BarContainer = Bar as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;
  const TooltipContainer = Tooltip as any;
  const CartesianGridContainer = CartesianGrid as any;
  const LegendContainer = Legend as any;
  const CellContainer = Cell as any;

  const frTooltipBarsRef = useRef<string[]>(selectedBars);
  const frLegendBarsRef = useRef<string[]>(selectedBars);
  const frLegendToggleRef = useRef<(barName: string) => void>((_n) => {});

  const toggleBarVisibility = useCallback((barName: string) => {
    setSelectedBars((prev) =>
      prev.includes(barName) ? prev.filter((d) => d !== barName) : [...prev, barName],
    );
  }, []);

  frLegendToggleRef.current = toggleBarVisibility;

  const frStableTooltipContent = useMemo(
    () => (props: any) => (
      <FirstResponseTooltip
        {...props}
        selectedBars={frTooltipBarsRef.current}
      />
    ),
    [],
  );

  const frStableLegendContent = useMemo(
    () => (legendProps: any) => (
      <FirstResponseLegend
        payload={legendProps?.payload}
        selectedBars={frLegendBarsRef.current}
        onToggle={(n) => frLegendToggleRef.current(n)}
      />
    ),
    [],
  );

  const styles: any = {
    statCard: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      padding: "16px 20px",
      border: "2px solid #e2e8f0",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      minWidth: "180px",
    },
    expandButton: {
      background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      transition: "all 0.3s",
      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  const handleFrChartMouseMove = useCallback((state: any) => {
    setHoveredBar((prev) => {
      const next =
        state?.isTooltipActive && state.activeTooltipIndex != null
          ? state.activeTooltipIndex
          : null;
      return prev === next ? prev : next;
    });
  }, []);

  const handleFrChartMouseLeave = useCallback(() => {
    setHoveredBar((prev) => (prev === null ? prev : null));
  }, []);

  const renderChart = (data: any[], height = "100px") => {
    frTooltipBarsRef.current = selectedBars;
    frLegendBarsRef.current = selectedBars;
    return (
      <ResponsiveChartContainer width="100%" height={height}>
        <BarChartContainer
          data={data}
          margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
          onMouseMove={handleFrChartMouseMove}
          onMouseLeave={handleFrChartMouseLeave}
        >
          <defs>
            <linearGradient id="withinSlaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.9} />
              <stop offset="95%" stopColor="#10b981" stopOpacity={0.7} />
            </linearGradient>
            <linearGradient id="breachedSlaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.9} />
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0.7} />
            </linearGradient>
          </defs>
          <CartesianGridContainer
            strokeDasharray="3 3"
            stroke="#e2e8f0"
            vertical={false}
          />
          <XAxisContainer
            dataKey="date"
            tick={{ fontSize: 12, fill: "#64748b", fontWeight: 500 }}
            tickLine={false}
            axisLine={{ stroke: "#cbd5e1", strokeWidth: 2 }}
            label={{
              value: "Date",
              position: "insideBottom",
              offset: -5,
              style: { fontSize: 13, fill: "#475569", fontWeight: 600 },
            }}
          />
          <YAxisContainer
            tick={{ fontSize: 12, fill: "#64748b", fontWeight: 500 }}
            tickLine={false}
            axisLine={{ stroke: "#cbd5e1", strokeWidth: 2 }}
            tickFormatter={(value: number) => Math.floor(value).toString()}
            label={{
              value: "Email count",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 13, fill: "#475569", fontWeight: 600 },
            }}
            allowDecimals={false}
            tickCount={4}
 
            domain={[
              0,
              (dataMax) => {
                if (!dataMax || dataMax === 0) return 3;
                return Math.ceil(dataMax / 2) * 2;
              },
            ]}
          />
          <TooltipContainer
            content={frStableTooltipContent}
            cursor={{ fill: "rgba(99, 102, 241, 0.12)" }}
          />
          <LegendContainer content={frStableLegendContent} />
          <BarContainer
            dataKey="Within SL"
            fill="url(#withinSlaGradient)"
            radius={[8, 8, 0, 0]}
            stackId="a"
            hide={!selectedBars.includes("Within SL")}
            isAnimationActive={false}
            animationDuration={1000}
          >
            {data.map((entry: any, index: number) => (
              <CellContainer
                key={`cell-within-${index}`}
                fill={hoveredBar === index ? "#059669" : "url(#withinSlaGradient)"}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (entry?.rawDate) {
                    setTableModalStage({ dateStr: entry.rawDate, value: "within_sla", label: entry.fullDate ?? entry.date });
                    setTableModalOpen(true);
                  }
                }}
              />
            ))}
          </BarContainer>
          <BarContainer
            dataKey="Breached SL"
            fill="url(#breachedSlaGradient)"
            radius={[8, 8, 0, 0]}
            stackId="a"
            hide={!selectedBars.includes("Breached SL")}
            isAnimationActive={false}
            animationDuration={1000}
          >
            {data.map((entry: any, index: number) => (
              <CellContainer
                key={`cell-breached-${index}`}
                fill={hoveredBar === index ? "#dc2626" : "url(#breachedSlaGradient)"}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (entry?.rawDate) {
                    setTableModalStage({ dateStr: entry.rawDate, value: "breached_sla", label: entry.fullDate ?? entry.date });
                    setTableModalOpen(true);
                  }
                }}
              />
            ))}
          </BarContainer>
        </BarChartContainer>
      </ResponsiveChartContainer>
    );
  };


  const hasData = chartData.length > 0;

  if (!hasData) {
    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          padding: "32px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <div
            style={{
              backgroundColor: "#e0e7ff",
              border: "1px solid #6366f1",
              borderRadius: "12px",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6366f1"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <div style={{ color: "#4338ca" }}>
              No data available for the selected date range. Please try a different date range or check back later.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "32px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                // fontSize: "1.75em",
                fontWeight: 700,
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6366f1"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {`First response SL: ${slaSection.overall_within_sla + slaSection.overall_breached_sla}`}
            </h2>
            <p
              style={{
                margin: "8px 0 0 0",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              Daily first response time metrics
            </p>
          </div>

          <button
            onClick={() =>
              setFullscreenChart({
                title: "First response SL",
              })
            }
            style={styles.expandButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(99, 102, 241, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(99, 102, 241, 0.3)";
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
            </svg>
            Expand View
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "24px",
            overflowX: "auto",
            paddingBottom: "8px",
            paddingTop: "8px",
          }}
        >
          <div
            style={{
              ...styles.statCard,
              borderColor: hoveredStat === 0 ? "#10b981" : "#e2e8f0",
              boxShadow:
                hoveredStat === 0 ? "0 8px 24px rgba(16, 185, 129, 0.25)" : "none",
              transform:
                hoveredStat === 0 ? "translateY(-4px)" : "translateY(0)",
            }}
            onMouseEnter={() => setHoveredStat(0)}
            onMouseLeave={() => setHoveredStat(null)}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "3px",
                  backgroundColor: "#10b981",
                  boxShadow: "0 0 8px rgba(16, 185, 129, 0.5)",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#64748b",
                }}
              >
                Within SL:{" "}
                <span style={{ fontSize: "16px", fontWeight: 600, color: "#10b981" }}>
                  {slaSection.overall_within_sla || 0}
                </span>
              </span>
              <span style={{ fontSize: "12px" }}>
                <span style={{ color: "#94a3b8" }}>Percentage: </span>
                <span style={{ fontWeight: 600, color: "#64748b" }}>
                  {withinSLAPercentage}%
                </span>
              </span>
            </div>
          </div>

          <div
            style={{
              ...styles.statCard,
              borderColor: hoveredStat === 1 ? "#ef4444" : "#e2e8f0",
              boxShadow:
                hoveredStat === 1 ? "0 8px 24px rgba(239, 68, 68, 0.25)" : "none",
              transform:
                hoveredStat === 1 ? "translateY(-4px)" : "translateY(0)",
            }}
            onMouseEnter={() => setHoveredStat(1)}
            onMouseLeave={() => setHoveredStat(null)}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "3px",
                  backgroundColor: "#ef4444",
                  boxShadow: "0 0 8px rgba(239, 68, 68, 0.5)",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#64748b",
                }}
              >
                Breached SL:{" "}
                <span style={{ fontSize: "16px", fontWeight: 600, color: "#ef4444" }}>
                  {slaSection.overall_breached_sla || 0}
                </span>
              </span>
              <span style={{ fontSize: "12px" }}>
                <span style={{ color: "#94a3b8" }}>Percentage: </span>
                <span style={{ fontWeight: 600, color: "#64748b" }}>
                  {breachedSLAPercentage}%
                </span>
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            height: "550px",
            backgroundColor: "#fafbfc",
            padding: "24px",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
          }}
        >
          {renderChart(chartData, "100%")}
        </div>
      </div>

      <FirstResponseFullscreenModal
        isOpen={fullscreenChart !== null}
        onClose={() => setFullscreenChart(null)}
        title={fullscreenChart?.title || "First response SL"}
        modalTotalSLA={modalTotalSLA}
        modalDateRange={modalDateRange}
        setModalDateRange={setModalDateRange}
        chartContent={renderChart(modalChartData, "100%")}
        modalTableData={modalTableData}
      />

      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={() => {
          setTableModalOpen(false);
          setTableModalStage(null);
          setClickToExpandPage(1);
        }}
        title={tableModalStage ? `First response SL – ${tableModalStage.value === "within_sla" ? "Within SL" : "Breached SL"} – ${tableModalStage.label}` : "Leads"}
        contentLabel="First response click-to-expand"
        emptyMessage="No emails in this segment."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx) => row?.thread_id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const threadId = row?.thread_id;
          if (threadId != null) {
            setTableModalOpen(false);
            setTableModalStage(null);
            navigate(`/email/inbox/${threadId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={[
          { label: "Subject", render: (row: any) => row?.subject ?? "—" },
          { label: "Contact Name", render: (row: any) => row?.contact_name ?? "—" },
          { label: "Contact Email", render: (row: any) => row?.contact_email ?? "—" },
          { label: "Contact Phone", render: (row: any) => row?.contact_phone ?? "—" },
          {
            label: "Created",
            render: (row: any) => {
              const raw = row?.created;
              if (!raw) return "—";
              const d = new Date(raw);
              if (Number.isNaN(d.getTime())) return "—";
              const dd = String(d.getDate()).padStart(2, "0");
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh = String(d.getHours()).padStart(2, "0");
              const min = String(d.getMinutes()).padStart(2, "0");
              const ss = String(d.getSeconds()).padStart(2, "0");
              return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
            },
          },
        ]}
      />
    </div>
  );
};

export default FirstResponse;